import React, { FC, memo, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import { Navigation, A11y, Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { default as SwiperClass } from "swiper";
import { imgCdnRoot } from "../../../config";
import { getCloudflareLoader } from "../../../helpers/cloudflareImages";
import {
  StyledAspectRatio,
  StyledAspectRatioWrapper,
  StyledNote,
  StyledWrapper,
} from "./index.styled";

interface PDPImagesProps {
  image: string;
  images?: {
    src?: string | null;
    pos?: number;
  }[];
  alt?: string;
  disableLoader?: boolean;
}

const Images: FC<PDPImagesProps> = ({
  image,
  images = [],
  alt = "",
  disableLoader = false,
}) => {
  const swiperRef = useRef<SwiperClass | null>(null);
  const options = {
    passiveListeners: false,
    slidesPerView: 1,
    navigation: {
      nextEl: "#images-next-slide",
      prevEl: "#images-prev-slide",
    },
    zoom: true,
    onSwiper: (swiper: SwiperClass) => (swiperRef.current = swiper),
  };

  const allImages = useMemo(
    () => [
      image,
      ...(images
        ? images
            .slice()
            .sort((a, b) => (a?.pos || 0) - (b?.pos || 0))
            .map((item) => item?.src)
        : []),
    ],
    [images, image]
  );

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  });

  return (
    <div>
      <StyledWrapper>
        <div className="swiper-button-custom--prev" id="images-prev-slide" />
        <Swiper
          {...options}
          modules={[Navigation, A11y, Zoom]}
          style={{ width: "100%" }}
        >
          {allImages.map((src) => (
            <SwiperSlide key={src} style={{ width: "100%" }}>
              <StyledAspectRatioWrapper>
                <StyledAspectRatio className="swiper-zoom-container">
                  <Image
                    src={src || ""}
                    fill
                    // allows for large sizes to accommodate zoom
                    sizes="auto"
                    loader={
                      disableLoader
                        ? undefined
                        : getCloudflareLoader(imgCdnRoot)
                    }
                    alt={alt}
                    priority
                  />
                </StyledAspectRatio>
              </StyledAspectRatioWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="swiper-button-custom--next" id="images-next-slide" />
      </StyledWrapper>
      <StyledNote>Double-tap or pinch image to zoom.</StyledNote>
    </div>
  );
};

export const PDPImages = memo(Images);
